// @ts-check

/**
 * @callback LinkedAccountList.OnRenameAccountFn
 * @param {{
 *   account: import("../../../types/api").AccountFromAPI,
 *   name: string
 * }} options
 */

/**
 * @callback LinkedAccountList.OnRemoveAccountFn
 * @param {{
 *   account: import("../../../types/api").AccountFromAPI,
 * }} options
 */

/**
 * @callback LinkedAccountList.OnRefreshProviderAccountFn
 * @param {{
 *   providerAccount: import("../../../types/api").ProviderAccountFromAPI,
 * }} options
 */

/**
 * @callback LinkedAccountList.OnEditProviderAccountFn
 * @param {{
 *   providerAccount: import("../../../types/api").ProviderAccountFromAPI,
 * }} options
 */

/**
 * @param {import("../view-services/provider-account-view-model.service").ProviderAccountViewModelService} ProviderAccountViewModelService
 * @param {import("../view-services/account-modes.constant").AccountModes} AccountModes
 * @constructor
 */
export function LinkedAccountListController(
  ProviderAccountViewModelService,
  AccountModes,
) {
  const ctrl = this;

  ///-- BINDINGS --///

  this.isDisabled = false;

  /** @type {import("../../../types/api").ProviderAccountFromAPI[]} */
  this.providerAccounts = [];
  /** @type {LinkedAccountList.OnRenameAccountFn} */
  this.onRenameAccount = () => null;
  /** @type {LinkedAccountList.OnRemoveAccountFn} */
  this.onRemoveAccount = () => null;

  this.onAddAccounts = () => {};
  /** @type {LinkedAccountList.OnRefreshProviderAccountFn} */
  this.onRefreshProviderAccount = () => null;
  /** @type {LinkedAccountList.OnEditProviderAccountFn} */
  this.onEditProviderAccount = () => null;

  ///-- END BINDINGS --///

  /** @type {import("types").ProviderAccountViewModel[]} */
  this.providerAccountVMs = [];
  /** @type {import('types').ProviderAccountViewModel.AccountViewModel[]} */
  this.accountVMs = [];

  /** @type {{ [key: string]: ActionButtonVM }} */
  this.actionButtons = {};

  this.showAccountsList = false;
  this.showHeaderButtons = true;
  this.providerAccountsWithPendingActions = {};

  /**
   * @param {import("types").ProviderAccountViewModel.AccountViewModel} accountVM
   */
  this.accountWasRenamed = function(accountVM, name) {
    return ctrl.onRenameAccount({ account: accountVM['@presenting'], name: name });
  };

  /** @param {import("../../../types").ProviderAccountViewModel.AccountViewModel} accountVM */
  this.removeAccount = function(accountVM) {
    return ctrl.onRemoveAccount({ account: accountVM['@presenting'] });
  };

  /**
   * @param {MouseEvent} event
   */
  this.addAccounts = function(event) {
    if (this.isDisabled) {
      event.preventDefault();
      return;
    }

    if (preventDefaultOnEvent(event)) {
      return this.onAddAccounts();
    }
  }


  /**
   * @param {import("../../../types").ProviderAccountViewModel} providerAccountVM
   * @param {MouseEvent} [$event]
   */
  this.editProviderAccountCredentials = function(providerAccountVM, $event) {
    if (preventDefaultOnEvent($event)) {
      ctrl.onEditProviderAccount({providerAccount: providerAccountVM['@presenting'] });
    }
  }

  /**
   * @param {import("../../../types").ProviderAccountViewModel} providerAccountVM
   * @param {MouseEvent} $event
   */
  this.actionButtonWasClicked = function(providerAccountVM, $event) {
    if (preventDefaultOnEvent($event)) {
      var actionButtonInfo = ctrl.actionButtonFor(providerAccountVM);

      // There USED to be JavaScript-related actions for the actionButton,
      // but as of 11MAY2020 there are no longer any.

      switch (actionButtonInfo.mode) {
        case "REFRESH":
        case "MFA":
          ctrl.onRefreshProviderAccount({providerAccount: providerAccountVM['@presenting'] });
          break;
        case "EDIT":
          return ctrl.editProviderAccountCredentials(providerAccountVM);
          break;
        default:
      }
    }
  };

  /**
   * @param {import('../../../types').ProviderAccountViewModel} providerAccountVM
   * @return {ActionButtonVM}
   */
  this.actionButtonFor = function( providerAccountVM ) {
    // console.groupCollapsed("actionButtonFor: " + providerAccountVM.institutionName);
    try {
      var providerAccount = providerAccountVM['@presenting'];

      // console.debug("providerAccountVM : ", providerAccountVM);

      /** @type {ActionButtonVM['mode']} */
      var mode = 'UNKNOWN';

      if (this.providerAccountsWithPendingActions[providerAccount.id]) {
        return {
          mode: 'PENDING',
          isEnabled: false,
          title: 'Pending',
          eventName: '',
          buttonClass: 'btn-default',
          popoverText: '',
          popoverEnabled: false,
          href: '',
          spinner: true,
        };
      }

      for (const modeType in AccountModes) {
        if (AccountModes[modeType](providerAccount)) {
          // @ts-ignore
          mode = modeType;
          break;
        }
      }

      var buttonClass = 'btn-default';
      var title = 'Unknown';
      var href = '';
      var eventName = '';
      var isEnabled = false;
      var spinner = false;
      var popoverText = '';

      switch (mode) {
        case 'REFRESH':
        case 'MFA':
          buttonClass = providerAccountVM.isRefreshStale ? 'btn-primary' : 'btn-default';
          title = 'Refresh';
          eventName = 'RefreshAccount';
          isEnabled = true;
          href = '/fastlink/refreshAccount/' + encodeURIComponent(providerAccountVM.id);
          break;
        case 'ERROR':
          buttonClass = 'btn-default';
          title = 'Error';
          isEnabled = false;
          popoverText = 'There seems to be a problem refreshing this account. Try clicking Connection > Edit credentials to refresh or try again later. If the issue persists reach out using the chat tool in the lower right of this page.';
          break;
        case 'EDIT':
          buttonClass = 'btn-warning';
          title = 'Fix';
          href = '/fastlink/editAccount/' + encodeURIComponent(providerAccountVM.id);
          eventName = 'EditAccount';
          isEnabled = true;
          break;
        case 'RECENT_REFRESH':
          buttonClass = 'btn-success';
          title = 'Recently Refreshed ✔️';
          popoverText = 'Account was recently refreshed.'
          href = '';
          isEnabled = false;
          break;
        case 'REFRESHING':
          buttonClass = 'btn-default';
          title = 'Refreshing';
          href = '';
          isEnabled = false;
          spinner = true;
          break;
      }

      return {
        mode,
        isEnabled,
        title,
        eventName,
        buttonClass,
        popoverText,
        popoverEnabled: popoverText != '',
        href,
        spinner,
      };
    } finally {
      // console.groupEnd();
    }
  }

  this.$onChanges = function(changes) {
    if (!!changes.providerAccounts) {
      this._updateVisibilities();
      this._buildViewModels();
    }
  }

  this.$onInit = function() {
    this._updateVisibilities();
    this._buildViewModels();
  }

  this._updateVisibilities = () => {
    this.showAccountsList = (!!this.providerAccounts && this.providerAccounts.length > 0);
    this.showHeaderButtons = this.showAccountsList;
  }

  this._buildViewModels = () => {
    this.providerAccountVMs =
      ProviderAccountViewModelService.buildModels(this.providerAccounts);
    this.actionButtons = this.providerAccountVMs
      .reduce(
        (memo, vm) => angular.merge(memo, {
          [vm.id]: this.actionButtonFor(vm),
        }),
        {},
      );
  }

  /**
   * @param {MouseEvent} [event]
   * @return boolean
   */
  function preventDefaultOnEvent(event) {
    if (!event) return true;

    if (!event.metaKey && !event.ctrlKey) {
      event.preventDefault();

      return true;
    }

    return false;
  }
}

// @ts-ignore
import html from './linked-account-list.template.html';

export default {
  template: html,
  controller: LinkedAccountListController,
  bindings: {
    isDisabled: "<",
    providerAccounts: "<",
    onRenameAccount: "&",
    onRemoveAccount: "&",
    onRefreshProviderAccount: "&",
    onEditProviderAccount: "&",
    onAddAccounts: "&"
  }
};

/**
 * @typedef {object} ActionButtonVM
 * @property {'REFRESH' | 'ERROR' | 'EDIT' | 'MFA' | 'REFRESHING' | 'RECENT_REFRESH' | 'UNKNOWN' | 'PENDING'} mode
 * @property {string} href
 * @property {boolean} isEnabled
 * @property {string} title
 * @property {string} eventName
 * @property {boolean} spinner
 * @property {string} popoverText
 * @property {boolean} popoverEnabled
 * @property {string} buttonClass
 */
